import { create } from 'zustand';
import { coreApi } from '@/integrations/api/active';
import { supabase } from '@/integrations/supabase/client';
import { useOperatingStore } from '@/modules/operating/operating-store';
import { useWorkspaceStore } from '@/modules/workspaces/workspace-store';
import type { Comment, CommentEntityType, CommentReaction, CommentThread } from '@/modules/comments/types';

const newId = () => crypto.randomUUID();

const entityKey = (t: CommentEntityType, id: string) => `${t}:${id}`;

let _commentsRequestEpoch = 0;

function commentFromRow(r: any): Comment {
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    entityType: r.entity_type,
    entityId: r.entity_id,
    rootCommentId: r.root_comment_id ?? null,
    replyToCommentId: r.reply_to_comment_id ?? null,
    authorUserId: r.author_user_id,
    authorMemberId: r.author_member_id ?? null,
    body: r.body,
    editedAt: r.edited_at ?? null,
    resolvedAt: r.resolved_at ?? null,
    resolvedByUserId: r.resolved_by_user_id ?? null,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

function reactionFromRow(r: any): CommentReaction {
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    commentId: r.comment_id,
    userId: r.user_id,
    emoji: r.emoji,
    createdAt: r.created_at,
  };
}

interface CommentsState {
  commentsByEntity: Record<string, Comment[]>;
  reactionsByComment: Record<string, CommentReaction[]>;
  loadingKeys: Record<string, boolean>;

  loadComments: (entityType: CommentEntityType, entityId: string) => Promise<void>;
  addComment: (args: {
    entityType: CommentEntityType;
    entityId: string;
    body: string;
    rootCommentId?: string | null;
    replyToCommentId?: string | null;
  }) => Promise<Comment | null>;
  editComment: (id: string, body: string) => Promise<void>;
  deleteComment: (id: string) => Promise<void>;
  resolveRoot: (id: string) => Promise<void>;
  reopenRoot: (id: string) => Promise<void>;
  addReaction: (commentId: string, emoji: string) => Promise<void>;
  removeReaction: (commentId: string, emoji: string) => Promise<void>;
  /** Drop everything on an account change (#142). */
  reset: () => void;
}

/** Comments-owned server cache, keyed by entity within the active workspace. */
export const useCommentsStore = create<CommentsState>((set, get) => {
  const replaceComment = async (id: string, path: string, body: Record<string, unknown>, method: 'POST' | 'PATCH') => {
    const { data, error } = await coreApi(path, body, method);
    const row = data?.[0];
    if (error || !row) return;
    const updated = commentFromRow(row);
    set((s) => {
      const key = entityKey(updated.entityType, updated.entityId);
      const list = (s.commentsByEntity[key] ?? []).map((c) => (c.id === id ? updated : c));
      return { commentsByEntity: { ...s.commentsByEntity, [key]: list } };
    });
  };

  return {
    commentsByEntity: {},
    reactionsByComment: {},
    loadingKeys: {},

    // Cleared on an account change (#142) — see AuthContext. Threads are keyed by
    // entity, not by user, so nothing about the cache says whose RLS returned it.
    reset: () => {
      _commentsRequestEpoch += 1;
      set({ commentsByEntity: {}, reactionsByComment: {}, loadingKeys: {} });
    },

    loadComments: async (entityType, entityId) => {
      const requestEpoch = _commentsRequestEpoch;
      const wsId = useWorkspaceStore.getState().currentWorkspaceId;
      if (!wsId) return;
      const key = entityKey(entityType, entityId);
      set((s) => ({ loadingKeys: { ...s.loadingKeys, [key]: true } }));

      const { data: cRows } = await supabase
        .from('comments')
        .select('*')
        .eq('workspace_id', wsId)
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .order('created_at', { ascending: true });

      if (
        requestEpoch !== _commentsRequestEpoch
        || useWorkspaceStore.getState().currentWorkspaceId !== wsId
      ) return;

      const comments = (cRows ?? []).map(commentFromRow);
      const ids = comments.map((c) => c.id);

      let reactions: CommentReaction[] = [];
      if (ids.length > 0) {
        const { data: rRows } = await supabase
          .from('comment_reactions')
          .select('*')
          .in('comment_id', ids);
        reactions = (rRows ?? []).map(reactionFromRow);
      }

      if (
        requestEpoch !== _commentsRequestEpoch
        || useWorkspaceStore.getState().currentWorkspaceId !== wsId
      ) return;

      const reactionsByComment: Record<string, CommentReaction[]> = {};
      for (const r of reactions) {
        (reactionsByComment[r.commentId] ??= []).push(r);
      }

      set((s) => ({
        commentsByEntity: { ...s.commentsByEntity, [key]: comments },
        reactionsByComment: { ...s.reactionsByComment, ...reactionsByComment },
        loadingKeys: { ...s.loadingKeys, [key]: false },
      }));
    },

    addComment: async ({ entityType, entityId, body, rootCommentId = null, replyToCommentId = null }) => {
      const wsId = useWorkspaceStore.getState().currentWorkspaceId;
      if (!wsId) return null;

      const { data, error } = await coreApi('/comments', {
        id: newId(),
        workspace_id: wsId,
        entity_type: entityType,
        entity_id: entityId,
        root_comment_id: rootCommentId,
        reply_to_comment_id: replyToCommentId,
        body,
        unlocked_cycle: useOperatingStore.getState().unlockedCycleKey ?? null,
      });
      const row = data?.[0];
      if (error || !row) return null;

      const c = commentFromRow(row);
      const key = entityKey(entityType, entityId);
      set((s) => ({
        commentsByEntity: {
          ...s.commentsByEntity,
          [key]: [...(s.commentsByEntity[key] ?? []), c],
        },
      }));
      return c;
    },

    editComment: (id, body) => replaceComment(id, `/comments/${id}`, { body }, 'PATCH'),

    deleteComment: async (id) => {
      let target: Comment | undefined;
      for (const list of Object.values(get().commentsByEntity)) {
        const found = list.find((c) => c.id === id);
        if (found) { target = found; break; }
      }
      if (!target) return;
      const { error } = await coreApi(`/comments/${id}`, {}, 'DELETE');
      if (error) return;
      set((s) => {
        const key = entityKey(target!.entityType, target!.entityId);
        const list = (s.commentsByEntity[key] ?? []).filter((c) => c.id !== id);
        const { [id]: _, ...restReactions } = s.reactionsByComment;
        return {
          commentsByEntity: { ...s.commentsByEntity, [key]: list },
          reactionsByComment: restReactions,
        };
      });
    },

    resolveRoot: (id) => replaceComment(id, `/comments/${id}/resolve`, {}, 'POST'),

    reopenRoot: (id) => replaceComment(id, `/comments/${id}/reopen`, {}, 'POST'),

    addReaction: async (commentId, emoji) => {
      const { data, error } = await coreApi(`/comments/${commentId}/reactions`, { id: newId(), emoji });
      const row = data?.[0];
      if (error || !row) return;
      const reaction = reactionFromRow(row);
      set((s) => ({
        reactionsByComment: {
          ...s.reactionsByComment,
          [commentId]: [...(s.reactionsByComment[commentId] ?? []), reaction],
        },
      }));
    },

    removeReaction: async (commentId, emoji) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { error } = await coreApi(`/comments/${commentId}/reactions`, { emoji }, 'DELETE');
      if (error) return;
      set((s) => ({
        reactionsByComment: {
          ...s.reactionsByComment,
          [commentId]: (s.reactionsByComment[commentId] ?? []).filter(
            (r) => !(r.userId === user.id && r.emoji === emoji),
          ),
        },
      }));
    },
  };
});

/** Group comments into threads. Replies go on their root regardless of reply_to depth. */
export function buildThreads(comments: Comment[]): CommentThread[] {
  const roots = comments.filter((c) => !c.rootCommentId).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  return roots.map((root) => ({
    root,
    replies: comments
      .filter((c) => c.rootCommentId === root.id)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
  }));
}

export function canEditOrDelete(
  comment: Comment,
  allComments: Comment[],
  currentUserId: string | null | undefined,
): boolean {
  if (!currentUserId || comment.authorUserId !== currentUserId) return false;
  // thread closed?
  const rootId = comment.rootCommentId ?? comment.id;
  const root = allComments.find((c) => c.id === rootId);
  if (root?.resolvedAt) return false;
  // Only the most recent comment in this thread is editable/deletable.
  const threadComments = allComments.filter(
    (c) => c.id === rootId || c.rootCommentId === rootId,
  );
  const latest = threadComments.reduce<Comment | null>(
    (acc, c) => (!acc || c.createdAt > acc.createdAt ? c : acc),
    null,
  );
  return latest?.id === comment.id;
}
